import { Component } from '@theme/component';

/**
 * Wishlist storage shared by every wishlist button on the page.
 * Products are kept client-side so the feature needs no app or customer account.
 *
 * Saved products are listed by handing the stored ids to Shopify's own search
 * as an `id:` query — the same trick the live Gecko theme uses. That keeps the
 * wishlist page server-rendered (real product cards, real pagination) instead
 * of re-implementing card markup in JS.
 */
const STORAGE_KEY = 'theme:wishlist';
const CHANGE_EVENT = 'theme:wishlist:change';
const VIEW = 'wishlist';

/* Links may be authored as /pages/wishlist (bottom nav, footer) or already
   point at the search view. Once seen, a link is tagged with
   data-wishlist-link so it keeps matching after its href is rewritten. */
const LINK_SELECTOR = '[data-wishlist-link], a[href$="/pages/wishlist"], a[href*="view=wishlist"]';

/** @returns {string[]} */
export function readWishlist() {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    return Array.isArray(value) ? value.map(String) : [];
  } catch {
    return [];
  }
}

/** @param {string[]} ids */
function writeWishlist(ids) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    /* storage unavailable (private mode) — the button still toggles visually */
  }
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { ids } }));
}

/** @returns {string} The search URL that renders the saved products. */
export function wishlistUrl() {
  const base = window.Theme?.routes?.search_url || '/search';
  const ids = readWishlist();
  const params = new URLSearchParams();
  params.set('view', VIEW);
  params.set('type', 'product');
  params.set('options[unavailable_products]', 'last');
  params.set('q', ids.map((id) => `id:${id}`).join(' OR '));
  return `${base}?${params}`;
}

/** @returns {boolean} True while the wishlist view is being displayed. */
function isWishlistView() {
  return new URLSearchParams(window.location.search).get('view') === VIEW;
}

/** Points every wishlist link at the current list and shows how many are saved. */
function refreshLinks() {
  const count = readWishlist().length;

  for (const link of document.querySelectorAll(LINK_SELECTOR)) {
    link.dataset.wishlistLink = '';
    link.setAttribute('href', wishlistUrl());

    let badge = link.querySelector('.wishlist-count');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'wishlist-count';
      link.appendChild(badge);
    }
    badge.textContent = String(count);
    badge.hidden = count === 0;
  }

  for (const el of document.querySelectorAll('[data-wishlist-count]')) {
    el.textContent = String(count);
  }

  /* The wishlist page is a search result, so an empty list has no query to
     run — the section renders a placeholder instead. */
  for (const el of document.querySelectorAll('[data-wishlist-empty]')) {
    el.hidden = count > 0;
  }
  for (const el of document.querySelectorAll('[data-wishlist-results]')) {
    el.hidden = count === 0;
  }
}

/**
 * A heart toggle rendered on product cards.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button - The toggle button.
 *
 * @extends {Component<Refs>}
 */
export class WishlistButtonComponent extends Component {
  requiredRefs = ['button'];

  connectedCallback() {
    super.connectedCallback();
    this.#render();
    document.addEventListener(CHANGE_EVENT, this.#render);
    window.addEventListener('pageshow', this.#render);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener(CHANGE_EVENT, this.#render);
    window.removeEventListener('pageshow', this.#render);
  }

  /** @returns {string} */
  get productId() {
    return this.dataset.productId ?? '';
  }

  /** Reflects the stored state onto the button. */
  #render = () => {
    const active = readWishlist().includes(this.productId);
    this.refs.button.setAttribute('aria-pressed', String(active));
    this.toggleAttribute('data-in-wishlist', active);

    const label = active ? this.dataset.labelRemove : this.dataset.labelAdd;
    if (label) this.refs.button.setAttribute('aria-label', label);
  };

  /**
   * Adds or removes this product from the wishlist.
   * @param {Event} [event]
   */
  toggle = (event) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.productId) return;

    const ids = readWishlist();
    const index = ids.indexOf(this.productId);

    if (index === -1) {
      ids.push(this.productId);
    } else {
      ids.splice(index, 1);
      /* On the wishlist page the card no longer belongs to the list it is
         sitting in, so drop it rather than leaving a hollow entry behind. */
      if (isWishlistView()) this.closest('.product-grid__item, li')?.remove();
    }

    writeWishlist(ids);
  };
}

if (!customElements.get('wishlist-button-component')) {
  customElements.define('wishlist-button-component', WishlistButtonComponent);
}

document.addEventListener(CHANGE_EVENT, refreshLinks);
window.addEventListener('pageshow', refreshLinks);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', refreshLinks);
} else {
  refreshLinks();
}
